import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { STATUS_LABEL, STATUS_CLASS } from "../projectMeta";

/* 간단한 인라인 아이콘 (stroke=currentColor) */
const I = {
  building: <path d="M3 21h18M5 21V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v16M9 8h.01M12 8h.01M9 12h.01M12 12h.01M9 16h.01M12 16h.01M19 21V10a1 1 0 0 0-1-1h-3" />,
  folder: <path d="M4 5h5l2 2h9a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z" />,
  quote: <><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M9 8h6M9 12h6M9 16h3" /></>,
  contract: <><path d="M7 3h7l4 4v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" /><path d="M13 3v5h5M9 14l2 2 4-4" /></>,
  invoice: <><rect x="5" y="3" width="14" height="18" rx="1" /><path d="M9 7h6M9 11h6M9 15h4M8 21v-3" /></>,
  mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,
  calendar: <><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M3 9h18M8 2v4M16 2v4" /></>,
  alert: <><path d="M12 3l9 16H3L12 3Z" /><path d="M12 10v4M12 17h.01" /></>,
  link: <><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" /><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" /></>,
  card: <><rect x="3" y="5" width="18" height="14" rx="2" /><circle cx="9" cy="11" r="2" /><path d="M6 16c.6-1.4 1.7-2 3-2s2.4.6 3 2M15 10h3M15 13h3" /></>,
  memo: <><path d="M5 3h10l4 4v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" /><path d="M8 10h8M8 14h8M8 18h5" /></>,
  forum: <><path d="M4 5h11a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H9l-4 3v-3H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z" /><path d="M16 9h4a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-1v3l-4-3h-4a1 1 0 0 1-1-1v-1" /></>,
  cart: <><circle cx="9" cy="20" r="1" /><circle cx="18" cy="20" r="1" /><path d="M2 3h3l2.4 12.4a1 1 0 0 0 1 .8h9.3a1 1 0 0 0 1-.8L21 7H6" /></>,
};

function Svg({ name, size = 24 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {I[name]}
    </svg>
  );
}

const TILES = [
  { key: "inquiries", label: "개발 문의", desc: "개발 의뢰 · 공동개발 문의 접수와 처리 상태", icon: "mail", to: "/sstoa/inquiries" },
  { key: "leads", label: "랜딩 문의", desc: "분야별 랜딩 상담 문의", icon: "mail", to: "/sstoa/leads" },
  { key: "landing", label: "랜딩페이지 관리", desc: "랜딩 주소 · 짧은 링크 · 유입 통계", icon: "link", to: "/sstoa/landing" },
  { key: "cards", label: "명함 관리", desc: "디지털 명함 · QR · 연락처", icon: "card", to: "/sstoa/cards" },
  { key: "site-products", label: "제품 관리", desc: "홈페이지 판매 상품", icon: "cart", to: "/sstoa/site-products" },
  { key: "site-works", label: "개발 사례 관리", desc: "홈페이지 개발 사례 · 공개 설정", icon: "folder", to: "/sstoa/site-works" },
  { key: "forum", label: "포럼 관리", desc: "포럼 글쓰기 · 첨부 파일 · 댓글 문의 답변", icon: "forum", to: "/sstoa/forum" },
  { key: "shop-orders", label: "판매 주문", desc: "판매제품 주문·입금·배송", icon: "cart", to: "/sstoa/shop-orders" },
  { key: "site-members", label: "회원 관리", desc: "홈페이지 가입 회원", icon: "building", to: "/sstoa/site-members" },
  { key: "companies", label: "업체 관리", desc: "거래처·담당자·문서", icon: "building", to: "/sstoa/companies" },
  { key: "projects", label: "프로젝트 관리", desc: "진행 상황·참여 업체", icon: "folder", to: "/sstoa/projects" },
  { key: "parts", label: "부품 재고", desc: "전자부품 재고 관리", icon: "cart", to: "/sstoa/parts" },
  { key: "quote", label: "견적서 관리", desc: "작성 중·보낸 견적서", icon: "quote", to: "/sstoa/quotes" },
  { key: "order", label: "발주서 관리", desc: "부품 구매·발주", icon: "cart", to: "/sstoa/orders" },
  { key: "invoice", label: "인보이스 관리", desc: "작성 중·보낸 인보이스", icon: "invoice", to: "/sstoa/invoices" },
  { key: "contract", label: "계약서 관리", desc: "작성 중·보낸 계약서", icon: "contract", to: "/sstoa/contracts" },
  { key: "documents", label: "전체 서류", desc: "모든 발행 내역", icon: "invoice", to: "/sstoa/documents" },
  { key: "email", label: "이메일 보내기", desc: "담당자에게 메일 발송", icon: "mail", to: "/sstoa/email" },
  { key: "schedule", label: "전체 일정 보기", desc: "프로젝트 일정·마감", icon: "calendar", to: "/sstoa/schedule" },
  { key: "issues", label: "이슈 보기", desc: "처리할 이슈 목록", icon: "alert", to: "/sstoa/issues" },
];

export default function SstoaHome() {
  const [stats, setStats] = useState({ companies: null, projects: null });
  const [projects, setProjects] = useState(null);
  const [pickMemo, setPickMemo] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [companies, projects] = await Promise.all([
          api.get("/companies"),
          api.get("/projects"),
        ]);
        setStats({ companies: companies.length, projects: projects.length });
        setProjects(projects);
      } catch {
        setStats({ companies: "—", projects: "—" });
      }
    })();
  }, []);

  return (
    <div>
      <div className="sstoa-topbar">
        <div>
          <h1 className="sstoa-title">SSTOA</h1>
          <p className="sstoa-sub">I.META 회사 관리 시스템 · 무엇을 하시겠어요?</p>
        </div>
        <button type="button" className="sstoa-btn sstoa-btn--primary sstoa-home-memo" onClick={() => setPickMemo(true)}>
          <Svg name="memo" size={16} />
          메모
        </button>
      </div>

      {/* 프로젝트를 고르면 그 프로젝트의 메모 탭으로 이동한다 — 프로젝트에서 메모 탭을 누른 것과 같은 화면 */}
      {pickMemo && (
        <ProjectPicker
          projects={projects}
          onPick={(p) => navigate(`/sstoa/projects/${p.id}?tab=memo`)}
          onClose={() => setPickMemo(false)}
        />
      )}

      <div className="sstoa-stats">
        <button className="sstoa-stat" onClick={() => navigate("/sstoa/companies")}>
          <span className="sstoa-stat__num">{stats.companies ?? "…"}</span>
          <span className="sstoa-stat__label">등록 업체</span>
        </button>
        <button className="sstoa-stat" onClick={() => navigate("/sstoa/projects")}>
          <span className="sstoa-stat__num">{stats.projects ?? "…"}</span>
          <span className="sstoa-stat__label">프로젝트</span>
        </button>
      </div>

      <div className="sstoa-tiles">
        {TILES.map((t) => (
          <button
            key={t.key}
            className={`sstoa-tile${t.soon ? " is-soon" : ""}`}
            onClick={() => navigate(t.to)}
          >
            <span className="sstoa-tile__icon"><Svg name={t.icon} /></span>
            <span className="sstoa-tile__body">
              <span className="sstoa-tile__label">
                {t.label}
                {t.soon && <span className="sstoa-tile__badge">준비 중</span>}
              </span>
              <span className="sstoa-tile__desc">{t.desc}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* 메모를 쓸 프로젝트 선택 팝업 — 이름으로 바로 걸러 보고, 누르면 이동한다 */
function ProjectPicker({ projects, onPick, onClose }) {
  const [q, setQ] = useState("");

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const needle = q.trim().toLowerCase();
  const rows = (projects || []).filter((p) => !needle || (p.name || "").toLowerCase().includes(needle));

  return (
    <div className="sstoa-modal" role="dialog" aria-modal="true" aria-label="메모할 프로젝트 선택"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sstoa-modal__box sstoa-picker">
        <div className="sstoa-modal__head">
          <h2 className="sstoa-modal__title">메모할 프로젝트 선택</h2>
          <button type="button" className="sstoa-modal__x" onClick={onClose} aria-label="닫기">×</button>
        </div>
        <div className="sstoa-picker__search">
          <input
            className="sstoa-input"
            placeholder="프로젝트명 검색"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && rows.length === 1) onPick(rows[0]); }}
            autoFocus
          />
        </div>
        <div className="sstoa-modal__body sstoa-picker__body">
          {projects === null ? (
            <div className="sstoa-empty">불러오는 중…</div>
          ) : rows.length === 0 ? (
            <div className="sstoa-empty">{needle ? "검색 결과가 없습니다." : "등록된 프로젝트가 없습니다."}</div>
          ) : (
            <ul className="sstoa-picker__list">
              {rows.map((p) => (
                <li key={p.id}>
                  <button type="button" className="sstoa-picker__item" onClick={() => onPick(p)}>
                    <span className="sstoa-picker__name">{p.name}</span>
                    <span className={`sstoa-tag ${STATUS_CLASS[p.status] || ""}`}>{STATUS_LABEL[p.status] || p.status}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
